import React, { useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Card, createStyles, InputLabel, makeStyles, TextField } from '@material-ui/core';
import { useHistory } from 'react-router-dom';

import { IAppState } from '../../../../store/rootDuck';
import { setLayoutFooter, setLayoutSubheader } from '../../../../utils/layout';
import { actions as blogActions } from '../../../../store/ducks/blog.duck';
import ButtonWithLoader from '../../../../components/ui/Buttons/ButtonWithLoader';
import homeStyles from '../../homeStyles';
import useCrudSnackbar from '../../../../hooks/useCrudSnackbar';
import RichEditor from '../../../../components/formComponents/RichEditor';
import { useEditorState, useFormatMessage, useTabs } from '../../../../hooks';
import FilesDropzone from '../../../../components/formComponents/FilesDropzone';
import { useCustomFormik } from './hooks/useCustomFormik';
import { Tabs } from './components/Tabs';
import { Container } from './components/Container';
import { TabPanel } from '../../../../components/other/Tab/TabPanel';

interface IField<T> {
  title: string;
  field: T;
}

export const useStyles = makeStyles(theme =>
  createStyles({
    actions: {
      marginTop: theme.spacing(3),
    },
  })
);

const CreatePublicationPage: React.FC<TPropsFromRedux> = ({
  loading,
  success,
  error,
  createPublication,
  clearCreatePublication,
}) => {
  const classes = useStyles();
  const homeClasses = homeStyles();
  const history = useHistory();
  const fm = useFormatMessage();

  const [editorState, setEditorState] = useEditorState();
  const [tabValue, changeTab] = useTabs();
  const [images, setImages] = useState<File[]>([]);

  const { values, handleBlur, handleChange, errors, touched, handleSubmit } = useCustomFormik({
    submit: createPublication,
    editorState,
    images,
  });

  const FIELDS: IField<keyof typeof values>[] = [
    { title: fm('title'), field: 'title' },
    { title: fm('description'), field: 'description' },
  ];

  setLayoutSubheader({
    title: fm('SUBMENU.PUBLICATIONS.ADD'),
  });
  setLayoutFooter({ show: true });

  useCrudSnackbar({
    success,
    error,
    clear: clearCreatePublication,
    successMessage: fm('SUCCESS'),
    errorMessage: `${fm('ERROR')}: ${error}`,
    afterSuccess: () => history.push('/blog/all'),
  });

  return (
    <Container>
      <Tabs tabValue={tabValue} changeTab={changeTab} />

      <form onSubmit={handleSubmit} className={homeClasses.form} autoComplete='off'>
        <TabPanel value={tabValue} index={0}>
          {FIELDS.map(item => {
            return (
              <TextField
                key={item.field}
                type='text'
                label={item.title}
                margin='normal'
                name={item.field}
                value={values[item.field]}
                multiline={item.field === 'description'}
                rows={item.field === 'description' ? 3 : 0}
                variant='outlined'
                onBlur={handleBlur}
                onChange={handleChange}
                helperText={touched[item.field] && errors[item.field]}
                error={Boolean(touched[item.field] && errors[item.field])}
              />
            );
          })}

          <InputLabel shrink style={{ marginTop: 7 }}>
            {fm('text')}
          </InputLabel>

          <RichEditor
            editorState={editorState}
            setEditorState={setEditorState}
            placeholder=''
            style={{ minHeight: 305 }}
          />

          <div className={classes.actions}>
            <ButtonWithLoader disabled={loading} loading={loading}>
              {fm('COMMON.BUTTON.SAVE')}
            </ButtonWithLoader>
          </div>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <div
            style={{
              padding: 40,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {images.length > 0 && (
              <Card style={{ marginBottom: 25 }}>
                <img
                  src={URL.createObjectURL(images[0])}
                  alt=''
                  style={{ maxWidth: 550, maxHeight: 500 }}
                />
              </Card>
            )}
            <FilesDropzone
              uploadFiles={(files: File[]) => setImages(files)}
              avaliableNumberOfFilesToUpload={1}
              title={fm('COMMON.PHOTO.LOAD')}
              withCrop
            />
          </div>
        </TabPanel>
      </form>
    </Container>
  );
};

const connector = connect(
  (state: IAppState) => ({
    loading: state.blog.createPublicationLoading,
    success: state.blog.createPublicationSuccess,
    error: state.blog.createPublicationError,
  }),
  {
    createPublication: blogActions.createPublicationRequest,
    clearCreatePublication: blogActions.clearCreatePublication,
  }
);

type TPropsFromRedux = ConnectedProps<typeof connector>;

export default connector(CreatePublicationPage);
